import React from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  label: string;
  value: string | number;
  delta?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: React.ReactNode;
  accent?: 'gold' | 'green' | 'blue' | 'amber' | 'red';
}

const accentMap: Record<NonNullable<Props['accent']>, string> = {
  gold:  'bg-primary-soft text-primary',
  green: 'bg-success-soft text-success',
  blue:  'bg-info-soft text-info',
  amber: 'bg-warning-soft text-warning',
  red:   'bg-danger-soft text-danger',
};

export const StatTile: React.FC<Props> = ({ label, value, delta, trend, icon, accent = 'gold' }) => (
  <div className="group relative overflow-hidden rounded-2xl border border-border bg-surface p-5 shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-elevated">
    {/* corner glow */}
    <div className={clsx(
      'pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100',
      accent === 'gold' && 'bg-primary',
      accent === 'green' && 'bg-success',
      accent === 'blue' && 'bg-info',
      accent === 'amber' && 'bg-warning',
      accent === 'red' && 'bg-danger',
    )} aria-hidden />

    <div className="relative flex items-start justify-between gap-3">
      <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{label}</div>
      {icon && (
        <div className={clsx('flex h-9 w-9 items-center justify-center rounded-lg', accentMap[accent])}>
          {icon}
        </div>
      )}
    </div>

    <div className="relative mt-3 flex items-baseline gap-2">
      <span className="font-display text-[28px] font-bold tracking-tight text-text-primary tabular-nums">
        {value}
      </span>
    </div>

    {delta && (
      <div className="relative mt-3 flex items-center gap-1.5">
        {trend === 'up' && <ArrowUpRight size={13} className="text-success" />}
        {trend === 'down' && <ArrowDownRight size={13} className="text-danger" />}
        <span className={clsx(
          'text-xs font-medium',
          trend === 'up' && 'text-success',
          trend === 'down' && 'text-danger',
          (!trend || trend === 'neutral') && 'text-text-muted',
        )}>{delta}</span>
      </div>
    )}
  </div>
);
